import { useRouter } from "next/compat/router";
import {
  Box,
  Modal,
  Button,
  InfoBox,
  IconFa,
} from "@cruk/cruk-react-components";

import { logOut } from "@fwa/src/services/auth";
import { useTracking } from "@fwa/src/hooks/useTracking";

import sharedStyles from "@fwa/src/components/styles.module.css";
import { faWarning } from "@fortawesome/free-solid-svg-icons";
import { useFundraiserContext } from "@fwa/src/contexts/FundraiserContext";

export function ModalWrongLogIn({
  modalName,
  componentName,
  closeFunction,
  isTeam = false,
}: {
  modalName: string;
  componentName: string;
  closeFunction: () => void;
  isTeam?: boolean;
}) {
  const { trackError } = useTracking();
  const [fundraiserState, setFundraiserState] = useFundraiserContext();
  const { fundraiser } = fundraiserState;
  const router = useRouter();

  // this modal is shown when a user tries to access a page that is not associated with their account,
  // so they are logged in but with the wrong account. The solution is to log out and log in with the correct account.
  // So we provide a button to log out and then reload the page which should trigger the log in flow again

  // we also show different error message depending on whether the user is logged with an oauth account
  // that either IS or IS NOT associated with a fundraiser.
  // this is to help supporter services troubleshoot the issue when users contact them about it.

  const title = !fundraiser
    ? "Incorrect Account"
    : !isTeam
      ? "Incorrect Fundraiser for Giving Page"
      : "Missing Fundraiser";

  const warning = !isTeam
    ? !fundraiser
      ? "This fundraising page is already associated with a different email address. Try logging in again with the correct email or contact supporter services if you need further help."
      : "You are logged in, but not as the owner of this page. If you want to edit this page please logout first and log in with the correct account."
    : !fundraiser
      ? "You are logged in, but your account is not associated with a fundraiser. Try logging in again with the correct email or contact supporter services if you need further help."
      : "You are logged in, but not as the owner of this page. If you want to edit this page please logout first and log in with the correct account."; // shouldn't ever get to this point because we don't show this modal in this scenario, but just in case

  return (
    <Modal
      closeFunction={closeFunction}
      modalName={modalName}
      showCloseButton
      data-component={componentName}
      startOpen
    >
      <div
        className="spacing-props"
        data-margin-top="xl"
        data-component="am-modal"
      >
        <Box marginBottom="xs">
          <Box marginBottom="xs">
            <InfoBox
              titleText={title}
              icon={<IconFa faIcon={faWarning} color="warning" />}
              descriptionText={warning}
            />
          </Box>
          <div className={sharedStyles["row-center"]}>
            <Button
              onClick={async () => {
                await logOut().catch((error) => {
                  trackError(error, {
                    component: "ModalWrongLogIn",
                    function: "Retry Log In Button onClick logOut",
                  });
                });
                setFundraiserState({
                  oauthUser: null,
                  fundraiser: null,
                  loggedInStatus: "loggedOut",
                });
                router?.reload();
                // when logged out, the items in the query string should trigger the log in flow again
              }}
            >
              Retry Log In
            </Button>
          </div>
        </Box>
        <Box marginBottom="xs">
          <div className={sharedStyles["row-center"]}>
            <Button
              appearance="secondary"
              onClick={() => {
                closeFunction();
              }}
              data-cta-type="dismiss-modal"
            >
              Explore my page
            </Button>
          </div>
        </Box>
      </div>
    </Modal>
  );
}
